import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { FilterSchema } from '@bingads-webui/filter-schema';
import { normalizeFilter, calculateDepth } from './converter/normalize';
import { convertToLegacy, parseFromLegacy } from './converter/legacy';
import { convertToOData } from './converter/odata-4';

/**
 * define Object OdataOptions
 * @typedef {Object} OdataOptions
 * @property {Object} [fieldMap={}] - fieldMap used for odata convertion.
 * @property {boolean|string[]} [ignoreCase=false] - ignoreCase for odata convertion.
 * @property {Object} custom - custom operator map for odata convertion.
 */

/**
 * define Object Options
 * @typedef {Object} Options
 * @property {Object} entitySchema - EntitySchema instance, required for legacy convertion.
 * @property {OdataOptions} odata - option object used for odata convertion.
 */

class FilterEntity {
  /**
   * FilterEntity object to convert query to different format.
   * @param {Object} query - the raw MongoDB query object, operator can only be $and or $or,
   *                         e.g. { $and: [{ field1: { $lt: 2} }, { field2: 'val' }], field3: 3 }
   * @param {Options} options - the options to be used when convert query.
   * @param {string} name - Filter name
   * @param {string} id - Filter id
  */
  constructor(
    query,
    {
      entitySchema, odata: {
        fieldMap = {},
        ignoreCase = false,
        custom,
      } = {},
    } = {}, name = null, id = null
  ) {
    this.query = normalizeFilter(query);
    this.odataOptions = { fieldMap, ignoreCase, custom };
    this.depth = calculateDepth(this.query);
    this.schema = entitySchema ? new FilterSchema(entitySchema) : {};
    this.name = name;
    this.id = id;
  }

  /**
   * Combine queries of two or more filter entities.
   * @param {Object[]} entities - array of FilterEntity instances
   * @param {string} operator - logic operator of how to connect queries,
   *                            should only be '$and' or '$or'.
   * @returns {Object} - combined query object.
   */
  static merge(entities, operator = '$and') {
    if (!_.isArray(entities)) {
      throw new Error('entities should be an array of FilterEntity instance.');
    }
    if (!entities.length) {
      return {};
    }
    if (operator !== '$and' && operator !== '$or') {
      throw new Error("operator should be '$and' or '$or'.");
    }
    const anotherOpe = operator === '$and' ? '$or' : '$and';
    let queries = [];
    _.each(entities, (entity) => {
      if (entity instanceof FilterEntity && !_.isEmpty(entity.query)) {
        const { query } = entity;
        if (query[operator]) {
          queries.push(...query[operator]);
        } else if (!_.isEmpty(query[anotherOpe]) && query[anotherOpe].length === 1) {
          queries.push(...query[anotherOpe]);
        } else {
          queries.push(query);
        }
      }
    });
    queries = putil.uniqWith(queries, _.isEqual);
    if (!queries.length) {
      return {};
    } else if (queries.length === 1) {
      return queries[0];
    }
    return { [operator]: queries };
  }

  /**
   * Convert query string to filter object.
   * @param {string} query -
   * filter string in url where we parse filter params from, contains filter params like
   * '&filter[3].SelectedColumn=Id&filter[3].SelectedOperator=Equal&filter[3].Values=30'
   * @param {Options} options - the options to be used in legacy conversion.
   * @returns {Object} - FilterEntity instance.
   */
  static fromQueryString(query, options) {
    let regex = /[\\?&]filter\[(\d)\]\.([^&#=]*)=([^&#]*)/g;
    if (!query.match(regex)) {
      regex = /[\\?&]filter%5B(\d)%5D\.([^&#=]*)=([^&#]*)/g;
    }
    const legacy = {};
    let result = regex.exec(query);
    while (result) {
      const index = parseInt(result[1], 10);
      const key = result[2];
      const value = key === 'Values' ? [result[3]] : result[3];
      if (key === 'Values' && legacy[index] && legacy[index].Values) {
        legacy[index].Values = _.union(legacy[index].Values, value);
      } else {
        legacy[index] = _.defaults(legacy[index] || {}, _.object([key], [value]));
      }
      result = regex.exec(query);
    }
    return FilterEntity.fromLegacy(_.values(legacy), options);
  }

  /**
 * Convert filter object to odata formatted.
 * @param {Object} filters - normalized filter object.
 * @param {Object} options - the options object used for odata convertion.
 * @param {Object} [options.fieldMap] - used for kendo convertion.
 * @param {boolean|string[]} [options.ignoreCase] - used for kendo convertion.
 * @param {Object} [options.custom] - custom operator map.
 * @param {Object} filterSchema - filter schema.
 * @returns {Object} - odata formatted filter object.
 */
  static toOdata(filters, { fieldMap = {}, ignoreCase, custom } = {
    fieldMap: {},
    ignoreCase: false,
  }, filterSchema) {
    const normalizedFilter = normalizeFilter(filters);
    return convertToOData(normalizedFilter, { fieldMap, ignoreCase, custom }, filterSchema);
  }

  /**
   * Convert legacy expression to filter object.
   * @param {Object[]} expressions - array of legacy expressions
   * @param {string} expressions[].SelectedColumn - column name
   * @param {string} expressions[].SelectedOperator - operator string, e.g. 'Equal'
   * @param {any[]} expressions.Values - array of values
   * @param {Object} options - the options to be used when convert query.
   * @param {Object} [options.entitySchema] - original entity schema object used to create
   *                                          FilterSchema, it's required for legacy convertion.
   * @param {OdataOptions} [options.odata] - option object used for odata format,
   *                                   including fieldMap, ignoreCase and custom.
   * @param {string} [name] - filter name
   * @param {string} [id] - filter id
   * @returns {Object} - FilterEntity object.
   */
  static fromLegacy(expressions, { entitySchema, odata } = {}, name, id) {
    const filterSchema = entitySchema ? new FilterSchema(entitySchema) : undefined;
    return new FilterEntity(
      parseFromLegacy(expressions, filterSchema),
      { filterSchema, odata }, name, id
    );
  }

  /**
   * Convert filter object to odata formatted.
   * @param {Object} options - the options object to be used for odata convertion.
   * @param {OdataOptions} [options.odata={}] - the options for odata convertion.
   * @returns {Object} - filter object in odata format.
   */
  odataV4({
    odata: {
      fieldMap = this.odataOptions.fieldMap,
      ignoreCase = this.odataOptions.ignoreCase,
      custom = this.odataOptions.custom,
    } = {},
  } = {}) {
    // The filter editor added the odata options to the filter.options
    // There should be general configuratoins,
    // and they can be overriden by the odata specific options.
    return convertToOData(this.query, { fieldMap, ignoreCase, custom }, this.schema);
  }

  /**
   * Convert filter object to legacy format.
   * @returns {Object} - filter object in legacy format.
   */
  legacy() {
    return convertToLegacy(this.query, this.depth, this.schema);
  }

  /**
   * Partially deep clone FilterEntity instance, query is deep cloned while others are reference.
   * @returns {Object} - copy of FilterEntity instance.
   */
  clone() {
    const newFilterEntity = new FilterEntity({});

    newFilterEntity.odataOptions = this.odataOptions;
    newFilterEntity.depth = this.depth;
    newFilterEntity.query = putil.cloneDeep(this.query);
    newFilterEntity.schema = this.schema;
    newFilterEntity.name = this.name;
    newFilterEntity.id = this.id;
    return newFilterEntity;
  }

  updateQuery(query) {
    this.query = normalizeFilter(query);
    this.depth = calculateDepth(this.query);
  }
}

export default FilterEntity;
